import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

import { ResponsiveLine } from '@nivo/line'
import { ChevronDownIcon, Loader2 } from 'lucide-react';
import moment from 'moment';
import { Label, Switch, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, Badge } from 'twyne-new.ui';

import { LOADING } from 'stores/BaseStore';

function formatHistogramDate(value) {
  if (value == null || value === '') return '';
  const m = moment(value);
  return m.isValid() ? m.format('MMM D, YYYY - hh:mm A') : String(value);
}

function useDarkMode() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'))
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'))
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return isDark
}

const propTypes = {
  histogramData: PropTypes.any,
  handleChangeHistogram: PropTypes.func,
};

const OPTIONS = [
  { value: "conversion", label: "Default (Conversion type)" },
  { value: "registrationCompleted", label: "Completed" },
  { value: "converted", label: "Converted" },
  { value: "duplicated", label: "Duplicated" },
]

const LABEL_MAP = {
  conversion: "Default (Conversion type)",
  registrationCompleted: "Completed",
  converted: "Converted",
  duplicated: "Duplicated",
}

const SERIES_COLORS = {
  awaiting: '#1f77b4',
  scrub_bucket: '#ff7f0e',
  converted: '#2ca02c',
  duplicated: '#d62728',
  fraud: '#9467bd',
}
function seriesColor(series) {
  const key = String(series.id).toLowerCase().replace(/\s+/g, '')
  return SERIES_COLORS[key] ?? '#1f77b4'
}

function Histogram({ status, data, onChangeHistogram, conversiontype }) {
  const [ratio, setRatio] = useState(false)
  const [convertedByValue, setConvertedByValue] = useState(conversiontype)
  const isDark = useDarkMode()
  const textColor = isDark ? '#e2e6e9' : '#1d2830'

  function handleChangeRatio() {
    setRatio(ratio => !ratio)
  }

  function handleChangeHistogram(value) {
    setConvertedByValue(value)
    onChangeHistogram(value)
  }

  if (ratio && data) {
    const amount = data.reduce((total, { data }) => {
      data.forEach(({ x: key, y: value }) => {
        total[key] = (total[key] || 0) + value
      })
      return total
    }, {})

    data = data.map(x => {
      const data = x.data.map(val => {
        const y = amount[val.x] > 0 ? (val.y / amount[val.x] * 100).toFixed(2) : 0
        return { ...val, y }
      })
      return { ...x, data }
    })
  }

  return <div className='relative h-[300px] border twy-border-base p-2 rounded' >
    <div className="absolute top-2 right-2 z-10 flex items-center gap-4 max-md:left-2 max-md:justify-between">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className='flex items-center gap-1 cursor-pointer'>
            <Badge variant='outline' size='lg'>
              <span><span className='max-md:hidden'>Converted By: </span>{LABEL_MAP[convertedByValue]}</span>
            </Badge>
            <ChevronDownIcon size={12} />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel className='md:hidden'>Converted By</DropdownMenuLabel>
          {OPTIONS.map(({ value, label }, index) => <DropdownMenuItem key={index} value={value} onClick={() => handleChangeHistogram(value)}>{label}</DropdownMenuItem>)}
        </DropdownMenuContent>
      </DropdownMenu>
      <div className='flex items-center gap-1'>
        <Label>Ratio:</Label>
        <Switch checked={ratio} onChange={handleChangeRatio} />
      </div>
    </div>

    {status === LOADING || !data ? <div className='flex items-center justify-center h-full'>
      <Loader2 size={24} className='animate-spin' />
    </div> : <ResponsiveLine
      data={data}
      colors={seriesColor}
      theme={{
        textColor,
        axis: {
          ticks: { text: { fill: textColor } },
          legend: { text: { fill: textColor } },
        },
        legends: { text: { fill: textColor } },
      }}
      margin={{
        top: 50,
        right: 110,
        bottom: 50,
        left: 60,
      }}
      xScale={{
        type: "point",
      }}
      yScale={{
        type: "linear",
        stacked: false,
        min: "auto",
        max: "auto",
      }}
      curve="monotoneX"
      axisTop={null}
      axisRight={null}
      axisBottom={{
        orient: "bottom",
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legendOffset: 36,
        legendPosition: "middle",
        format: formatHistogramDate,
      }}
      pointTooltip={({ point }) => <div style={{
        background: 'white',
        padding: '9px 12px',
        border: '1px solid #ccc',
        borderRadius: 4,
        boxShadow: '0 2px 4px rgba(0,0,0,.15)',
      }}
      >
        <div style={{ marginBottom: 4, fontWeight: 600 }}>{formatHistogramDate(point.data.x)}</div>
        <div>{String(point.serieId)}: {ratio ? `${Number(point.data.y).toFixed(2)} %` : point.data.y}</div>
      </div>
      }
      axisLeft={{
        format: value => ratio ? `${value.toFixed(0)} %` : value,
        orient: "left",
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: "count",
        legendOffset: -50,
        legendPosition: "middle",
      }}
      enableGridY={false}
      enableDots={true}
      dotSize={5}
      dotColor="inherit:darker(0.3)"
      enableArea={true}
      areaBlendMode="darken"
      areaOpacity={0.05}
      animate={true}
      motionStiffness={90}
      motionDamping={15}
      legends={[
        {
          anchor: "bottom-right",
          direction: "column",
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: "left-to-right",
          itemWidth: 80,
          itemHeight: 20,
          itemOpacity: 0.75,
          itemTextColor: textColor,
          symbolSize: 12,
          symbolShape: "circle",
          symbolBorderColor: "rgba(0, 0, 0, .5)",
          effects: [
            {
              on: "hover",
              style: {
                itemBackground: "rgba(0, 0, 0, .03)",
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
    />}
  </div>
}

Histogram.propTypes = propTypes

export default Histogram
